import { Stack } from '@mui/material';
import { ReactNode } from 'react';

import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { HouseholdValidationQuestionsBanner } from '@/modules/validationQuestions/ValidationQuestionsBanner/HouseholdValidationQuestionsBanner';
import { ValidationQuestionsBanner } from '@/modules/validationQuestions/ValidationQuestionsBanner/ValidationQuestionsBanner';

import {
  ClientOverviewPageContent,
  ClientOverviewPageContentProps,
} from './ClientOverviewPageContent';
import { useHouseholdSetupCompletion } from './hooks/useHouseholdSetupCompletion';
import { useHouseholdSetupPhase } from './hooks/useHouseholdSetupPhase';
import { HouseholdNoDocumentsBanner } from './HouseholdNoDocumentsBanner';
import { HouseholdOnboardingHost } from './HouseholdOnboardingHost';
import { HouseholdSetupLoading } from './HouseholdSetupLoading';
import { HouseholdSetupProgress } from './HouseholdSetupProgress';
import { HouseholdSetupSkeleton } from './HouseholdSetupSkeleton';
import { SetupValidationQuestionsHost } from './validationQuestions/SetupValidationQuestionsHost';

interface EnhancedHouseholdOverviewProps
  extends Omit<
    ClientOverviewPageContentProps,
    'setupContent' | 'showReviewSuggestions'
  > {
  overviewLoading: boolean;
  overviewFailed: boolean;
  hasNoDocuments: boolean | undefined;
  refreshOverview: () => Promise<unknown>;
  header?: ReactNode;
}

/** Mounted only when enhanced onboarding is enabled, keyed by household ID. */
export function EnhancedHouseholdOverview(
  props: EnhancedHouseholdOverviewProps
) {
  const setup = useHouseholdSetupPhase(props.householdId);
  return (
    <>
      <SetupValidationQuestionsHost
        householdId={props.householdId}
        householdName={props.household?.displayName ?? 'client'}
        conversationId={setup.conversationId}
        state={setup.validationQuestionsState}
        onAnswersAccepted={setup.onAnswersAccepted}
      />
      <HouseholdOnboardingHost
        householdId={props.householdId}
        household={props.household}
      />
      <EnhancedOverviewContent {...props} setup={setup} />
    </>
  );
}

function EnhancedOverviewContent(
  props: EnhancedHouseholdOverviewProps & {
    setup: ReturnType<typeof useHouseholdSetupPhase>;
  }
) {
  const {
    householdId,
    household,
    overviewLoading,
    overviewFailed,
    hasNoDocuments,
    setup,
  } = props;
  const canEditHousehold = useCanEditHouseholdData();
  const initialSetup = setup.isInitialSetup && canEditHousehold;
  const completion = useHouseholdSetupCompletion({
    householdId,
    onboardingCompletionTrackerId: setup.onboardingCompletionTrackerId,
    overviewRefreshTrackerIds:
      setup.phase === 'initializing' ? [] : setup.overviewRefreshTrackerIds,
    refreshOverview: props.refreshOverview,
  });
  const unavailable =
    setup.phase === 'unavailable' || overviewFailed || completion.refreshFailed;
  const showNoDocuments =
    hasNoDocuments &&
    !unavailable &&
    !setup.conversationId &&
    !setup.preparingQuestions &&
    (setup.phase === 'idle' || setup.phase === 'complete');
  const noDocumentsBanner = showNoDocuments ? (
    <HouseholdNoDocumentsBanner householdId={householdId} />
  ) : null;
  if (
    !unavailable &&
    (setup.phase === 'initializing' || (overviewLoading && !household))
  ) {
    return (
      <>
        <HouseholdValidationQuestionsBanner
          householdId={householdId}
          showBanner={false}
        />
        <HouseholdSetupSkeleton />
      </>
    );
  }
  if (unavailable || setup.phase === 'idle') {
    // Each failed request reports through useReportError (diagnostics.error).
    // Keep the normal overview usable without a second setup-specific error UI.
    return (
      <Stack>
        <HouseholdValidationQuestionsBanner
          householdId={householdId}
          showBanner={!setup.submitted}
        />
        {props.header}
        {noDocumentsBanner}
        <Stack p={2}>
          <ClientOverviewPageContent {...props} />
        </Stack>
      </Stack>
    );
  }
  const householdName = household?.displayName ?? 'client';
  let loadingScreen: 'extracting' | 'questions' | 'preparing' | undefined;
  if (setup.conversationId) loadingScreen = 'questions';
  else if (setup.phase === 'extracting') loadingScreen = 'extracting';
  else if (setup.preparingQuestions) loadingScreen = 'preparing';
  const showCompletion =
    setup.phase === 'complete' &&
    hasNoDocuments === false &&
    !completion.dismissed &&
    completion.readyForCompletion;
  const loading =
    loadingScreen && setup.snapshot ? (
      <HouseholdSetupLoading
        householdName={householdName}
        snapshot={setup.snapshot}
        initialSetup={initialSetup}
        firstDocumentUpload={setup.isFirstDocumentUpload}
        preparingQuestions={loadingScreen === 'preparing'}
        validationPrompt={
          setup.conversationId ? (
            <ValidationQuestionsBanner
              householdId={householdId}
              conversationId={setup.conversationId}
              variant="setup"
              householdName={householdName}
              initialSetup={setup.isInitialSetup}
            />
          ) : undefined
        }
      />
    ) : null;
  if (initialSetup && loading) {
    return (
      <>
        <HouseholdValidationQuestionsBanner
          householdId={householdId}
          showBanner={!setup.submitted && !setup.conversationId}
        />
        <Stack px={{ xs: 0, md: 3 }} pt={{ xs: 2, md: 5 }}>
          {loading}
        </Stack>
      </>
    );
  }
  return (
    <Stack>
      <HouseholdValidationQuestionsBanner
        householdId={householdId}
        showBanner={!setup.submitted && (!loading || !setup.conversationId)}
      />
      {props.header}
      {noDocumentsBanner}
      <Stack p={2}>
        <ClientOverviewPageContent
          {...props}
          showReviewSuggestions={!showCompletion}
          setupContent={
            loading ?? (
              <HouseholdSetupProgress
                householdId={householdId}
                householdName={householdName}
                profileRunning={setup.profileRunning}
                presentationRunning={setup.presentationRunning}
                presentationCount={setup.presentationCount}
                singlePresentationId={setup.singlePresentationId}
                onboardingRunning={setup.onboardingRunning}
                onboardingFailed={setup.onboardingFailed}
                processingStartedAt={setup.processingStartedAt}
                hasPresentation={setup.hasPresentation}
                presentationFailed={setup.presentationFailed}
                showCompletion={showCompletion}
                onDismissCompletion={completion.dismiss}
              />
            )
          }
        />
      </Stack>
    </Stack>
  );
}
